import React, { Component } from "react";

// Standard questionnaire data from ProSelect Contract March 2022, Pages 6-8
// Exported so ispForm.jsx can build the riskQA array for the ISP document
export const CAPACITY_QUESTIONS = [
  {
    id: "cap1",
    text: "Time horizon is the minimum time the account is expected to be invested before you will need to start spending the money you are investing. What is the time horizon of this account?",
    answers: [
      { text: "Less than 1 Year", score: 0 },
      { text: "1 - 5 Years", score: 3 },
      { text: "6 - 10 Years", score: 6 },
      { text: "11 - 25 Years", score: 9 },
      { text: "More than 25 Years", score: 13 },
    ],
  },
  {
    id: "cap2",
    text: "How likely are you to use this account to fund unforeseen major purchases and/or emergencies?",
    answers: [
      { text: "I plan to use this account.", score: 0 },
      { text: "I might use this account.", score: 4 },
      { text: "I am unlikely to use this account.", score: 8 },
      { text: "Under no circumstances would I use this account.", score: 13 },
    ],
  },
  {
    id: "cap3",
    text: "Once you begin to withdraw funds from this account, what is the expected duration for these withdrawals?",
    answers: [
      { text: "Less than 3 Years", score: 0 },
      { text: "3 - 5 Years", score: 4 },
      { text: "6 - 10 Years", score: 8 },
      { text: "More than 10 years", score: 12 },
    ],
  },
  {
    id: "cap4",
    text: "To what extent will your standard of living be impacted by this investment?",
    answers: [
      {
        text: "It will have a significant impact. I cannot afford for this investment to not meet my goals.",
        score: 0,
      },
      {
        text: "It will have a moderate impact. It will cause me to reassess my standard of living.",
        score: 6,
      },
      {
        text: "It will have a minimal impact. I do not depend on this account to maintain my lifestyle.",
        score: 12,
      },
    ],
  },
];

export const WILLINGNESS_QUESTIONS = [
  {
    id: "will1",
    text: "Which of the following statements best describes your views when considering a new investment?",
    answers: [
      {
        text: "Preservation of capital is more important to me than investment return. I am willing to have lower returns in order to avoid losing principal.",
        score: 0,
      },
      {
        text: "Preservation of capital is important to me, but I am willing to assume some risk to achieve a slightly higher return.",
        score: 2,
      },
      {
        text: "While preservation of capital is important to me, I consider the growth of my account to be my primary objective.",
        score: 5,
      },
      {
        text: "Capital growth is most important to me, and I am willing to withstand significant fluctuations in capital.",
        score: 7,
      },
      {
        text: "I seek to obtain the highest growth possible, and I am willing to withstand extreme fluctuations in capital.",
        score: 10,
      },
    ],
  },
  {
    id: "will2",
    text: "Investments can go up or down in value, and experts often say you should be prepared to weather a downturn. By how much could the total value of all your investments go down before you would move to a more conservative allocation?",
    answers: [
      { text: "Any decline would make me feel uncomfortable.", score: 0 },
      { text: "10%", score: 2 },
      { text: "20%", score: 4 },
      { text: "33%", score: 6 },
      { text: "50%", score: 8 },
      { text: "More than 50%", score: 10 },
    ],
  },
  {
    id: "will3",
    text: "Over time, inflation can significantly impact the amount of goods and services you are able to purchase. What is your attitude concerning your portfolio and inflation?",
    answers: [
      {
        text: "I am more concerned about the risk of short-term loss, realizing that my investments may not keep pace with inflation.",
        score: 0,
      },
      {
        text: "I anticipate a return that modestly exceeds the rate of inflation. I am willing to take on a moderate level of risk to achieve that return.",
        score: 5,
      },
      {
        text: "I anticipate a return that significantly exceeds the rate of inflation. I am willing to tolerate higher capital fluctuations in order to achieve this goal.",
        score: 10,
      },
    ],
  },
  {
    id: "will4",
    text: "Investments which produce above-average returns typically experience above-average risk. With this in mind, what percentage of these assets would you be willing to allocate to investments where both returns and risks are expected to be above average?",
    answers: [
      { text: "0%", score: 0 },
      { text: "10%", score: 1 },
      { text: "20%", score: 2 },
      { text: "30%", score: 3 },
      { text: "40%", score: 4 },
      { text: "50%", score: 5 },
      { text: "60%", score: 6 },
      { text: "70%", score: 7 },
      { text: "80%", score: 8 },
      { text: "90%", score: 9 },
      { text: "100%", score: 10 },
    ],
  },
  {
    id: "will5",
    text: "The following chart shows the potential values of five hypothetical investments of $100,000 over one year. Based on these ranges, which account would you prefer?",
    answers: [
      {
        text: "Account A: Potential gain = $7,000 / Potential loss = $3,000",
        score: 0,
      },
      {
        text: "Account B: Potential gain = $9,000 / Potential loss = $4,000",
        score: 2,
      },
      {
        text: "Account C: Potential gain = $13,000 / Potential loss = $6,000",
        score: 5,
      },
      {
        text: "Account D: Potential gain = $16,000 / Potential loss = $9,000",
        score: 7,
      },
      {
        text: "Account E: Potential gain = $20,000 / Potential loss = $12,000",
        score: 10,
      },
    ],
  },
];

const SCORE_MAPPING = [
  { min: 0, max: 35, category: "Conservative", color: "#5b7fa5" },
  { min: 36, max: 65, category: "Moderate", color: "#385EA4" },
  { min: 66, max: 100, category: "Growth", color: "#1a3a6b" },
];

const RISK_CATEGORY_PREFIXES = {
  Conservative: "conservative",
  Moderate: "moderate",
  Growth: "growth",
};

export function getPortfolioRiskCategory(portfolio, aliases = {}) {
  const normalizedPortfolio = String(portfolio || "").trim().toLowerCase();
  const aliasEntry = Object.keys(aliases || {}).find(
    (key) => key.trim().toLowerCase() === normalizedPortfolio,
  );
  if (aliasEntry) return aliases[aliasEntry];

  return Object.keys(RISK_CATEGORY_PREFIXES).find((category) =>
    normalizedPortfolio.startsWith(RISK_CATEGORY_PREFIXES[category]),
  );
}

export function portfolioMatchesRiskCategory(
  portfolio,
  riskCategory,
  aliases = {},
) {
  const effectiveCategory = getPortfolioRiskCategory(portfolio, aliases);
  if (effectiveCategory) return effectiveCategory === riskCategory;

  const prefix = RISK_CATEGORY_PREFIXES[riskCategory];
  if (!prefix) return false;
  return String(portfolio || "").trim().toLowerCase().startsWith(prefix);
}

export function getPortfoliosForRiskCategory(
  portfolios,
  riskCategory,
  aliases = {},
) {
  return (portfolios || []).filter((portfolio) =>
    portfolioMatchesRiskCategory(portfolio, riskCategory, aliases),
  );
}

class RiskQuestionnaire extends Component {
  getRiskCategory = (score) => {
    for (const range of SCORE_MAPPING) {
      if (score >= range.min && score <= range.max) return range;
    }
    return SCORE_MAPPING[0];
  };

  handleAnswer = (section, questionId, score) => {
    this.props.onAnswerChange(section, questionId, score);
  };

  renderQuestion = (question, section, index) => {
    const answers = this.props[`${section}Answers`] || {};
    const selectedScore = answers[question.id];
    const isAnswered = selectedScore !== undefined;
    // Question 4 in willingness (will4) has many short options - render inline
    const isCompactOptions = question.id === "will4";

    return (
      <div key={question.id} className="mb-3">
        <p className="mb-2">
          <strong>{index + 1}.</strong> {question.text}
        </p>
        <div
          style={{
            borderLeft: `5px solid ${isAnswered ? "green" : "#ced4da"}`,
            paddingLeft: "12px",
          }}
        >
          {question.id === "will5" && this.props.riskChartUrl && (
            <div style={{ margin: "12px 0 16px 8px" }}>
              <img
                src={this.props.riskChartUrl}
                alt="One-Year Range of Hypothetical Account Values"
                style={{ maxWidth: "min(420px, 100%)", height: "auto" }}
              />
            </div>
          )}
          {isCompactOptions ? (
            <div className="row" style={{ marginLeft: "8px" }}>
              {question.answers.map((answer, aIdx) => (
                <div className="col-sm-4 col-md-3 col-lg-2" key={aIdx}>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name={question.id}
                      id={`${question.id}_${aIdx}`}
                      checked={selectedScore === answer.score}
                      onChange={() =>
                        this.handleAnswer(section, question.id, answer.score)
                      }
                      style={{ height: "15px", width: "15px" }}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`${question.id}_${aIdx}`}
                    >
                      {answer.text}{" "}
                      <span className="text-muted">({answer.score})</span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ marginLeft: "8px" }}>
              {question.answers.map((answer, aIdx) => (
                <div className="form-check" key={aIdx}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name={question.id}
                    id={`${question.id}_${aIdx}`}
                    checked={selectedScore === answer.score}
                    onChange={() =>
                      this.handleAnswer(section, question.id, answer.score)
                    }
                    style={{ height: "15px", width: "15px" }}
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`${question.id}_${aIdx}`}
                  >
                    {answer.text}{" "}
                    <span className="text-muted">({answer.score})</span>
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  renderScoreBar = (score, max, label) => {
    const pct = max > 0 ? (score / max) * 100 : 0;
    return (
      <div className="mb-2">
        <div className="d-flex justify-content-between">
          <span style={{ fontSize: "15px" }}>
            <strong>{label}</strong>
          </span>
          <span style={{ fontSize: "15px" }}>
            <strong>{score}</strong> / {max}
          </span>
        </div>
        <div className="progress" style={{ height: "12px" }}>
          <div
            className="progress-bar"
            role="progressbar"
            style={{ width: `${pct}%`, backgroundColor: "#385EA4" }}
            aria-valuenow={score}
            aria-valuemin="0"
            aria-valuemax={max}
          />
        </div>
      </div>
    );
  };

  render() {
    const {
      capacityAnswers,
      willingnessAnswers,
      capacityTotal,
      willingnessTotal,
      combinedScore,
      riskCategory,
      selectedPortfolio,
      portfolioOverride,
      overrideReason,
      overrideOtherText,
      onPortfolioChange,
      onOverrideToggle,
      onOverrideReasonChange,
      onOverrideOtherTextChange,
      onReset,
      riskToleranceOptions,
      riskToleranceAliases,
    } = this.props;

    const hasAnyRiskInput =
      Object.keys(capacityAnswers || {}).length > 0 ||
      Object.keys(willingnessAnswers || {}).length > 0 ||
      selectedPortfolio ||
      portfolioOverride ||
      overrideReason ||
      overrideOtherText;
    const categoryInfo = this.getRiskCategory(combinedScore);
    const capacityComplete = CAPACITY_QUESTIONS.every(
      (q) => capacityAnswers[q.id] !== undefined,
    );
    const willingnessComplete = WILLINGNESS_QUESTIONS.every(
      (q) => willingnessAnswers[q.id] !== undefined,
    );
    const allAnswered = capacityComplete && willingnessComplete;

    // Portfolio options come from the strategy's taxableBonds/municipalBonds arrays
    // (passed in as riskToleranceOptions from ispForm based on strategy + bond type).
    // When override is active, show all options. Otherwise, only show portfolios
    // aligned with the scored risk category; if none exist, require override.
    const allOptions = riskToleranceOptions || [];
    const matchingPortfolios = getPortfoliosForRiskCategory(
      allOptions,
      riskCategory,
      riskToleranceAliases,
    );
    const availablePortfolios = portfolioOverride
      ? allOptions
      : matchingPortfolios;
    const scoredPortfolioUnavailable =
      this.props.strategySelected &&
      !portfolioOverride &&
      riskCategory &&
      allOptions.length > 0 &&
      matchingPortfolios.length === 0;
    const selectedPortfolioAvailable =
      !!selectedPortfolio && availablePortfolios.includes(selectedPortfolio);

    return (
      <div>
        {onReset && (
          <div className="d-flex justify-content-end mb-3">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={onReset}
              disabled={!hasAnyRiskInput}
            >
              Reset Risk Questionnaire
            </button>
          </div>
        )}

        {/* Step 1: Capacity for Risk */}
        <label style={{ color: "#385EA4", fontSize: "20px" }}>
          <strong>Step 1: Determine your Capacity for Risk</strong>
        </label>
        <small className="form-text text-muted mb-3 d-block">
          Your Capacity for Risk is defined as your financial ability to sustain
          risk. For accuracy, it is mandatory that each question is answered.
        </small>

        {CAPACITY_QUESTIONS.map((q, i) =>
          this.renderQuestion(q, "capacity", i),
        )}

        <div className="mb-4" />

        {/* Step 2: Willingness to Accept Risk */}
        <label style={{ color: "#385EA4", fontSize: "20px" }}>
          <strong>Step 2: Determine your Willingness to Accept Risk</strong>
        </label>
        <small className="form-text text-muted mb-3 d-block">
          Your Willingness to Accept Risk is defined as your emotional ability
          to handle risk. For accuracy, it is mandatory that each question is
          answered.
        </small>

        {WILLINGNESS_QUESTIONS.map((q, i) =>
          this.renderQuestion(q, "willingness", i),
        )}

        <div className="mb-4" />

        {/* Step 3: Combined Assessment */}
        <label style={{ color: "#385EA4", fontSize: "20px" }}>
          <strong>Step 3: Total Risk Tolerance Score</strong>
        </label>

        {allAnswered ? (
          <div>
            {/* Score summary — component subtotals come first, with the
                combined score separated underneath as the final result. */}
            <div className="card card-body mb-3">
              <div className="row text-center">
                <div className="col-md-6">
                  <small className="text-muted">Capacity for Risk</small>
                  <div>
                    <strong>{capacityTotal}</strong>
                    <small className="text-muted"> / 50</small>
                  </div>
                </div>
                <div className="col-md-6">
                  <small className="text-muted">
                    Willingness to Accept Risk
                  </small>
                  <div>
                    <strong>{willingnessTotal}</strong>
                    <small className="text-muted"> / 50</small>
                  </div>
                </div>
              </div>

              <div
                className="text-center mt-2"
                style={{ borderTop: "1px solid #dee2e6", paddingTop: "10px" }}
              >
                <small className="text-muted">Total Risk Tolerance Score</small>
                <h2 className="mb-0" style={{ color: "#385EA4" }}>
                  {combinedScore}
                  <small className="text-muted" style={{ fontSize: "16px" }}>
                    {" "}
                    / 100
                  </small>
                </h2>
              </div>

              {/* Category-range visualization is its own block, with an
                  explicit heading so it isn't read as a per-component
                  breakdown of the scores above. */}
              <div
                className="mt-3"
                style={{ borderTop: "1px solid #dee2e6", paddingTop: "10px" }}
              >
                <small className="text-muted d-block mb-2 text-center">
                  Risk Category Range
                </small>
                {/* Risk category scale — same asset rendered on the ISP PDF
                  (public/Images/risk-scale.png) so the on-screen and printed
                  scales match. The arrow indicator sits absolute-positioned
                  beneath it at left:${combinedScore}% so it tracks the 0–100
                  score axis built into the image. */}
                <div style={{ position: "relative" }}>
                  <img
                    src="/Images/risk-scale.png"
                    alt="Risk category scale: Conservative (0-35), Moderate (36-65), Growth (66-100)"
                    style={{ width: "100%", display: "block" }}
                  />
                  {/* Black SVG diamond pointer — centered on the bar's vertical
                    midline (y ≈ 33.65% from image top, measured from the
                    saturated-blue region of risk-scale.png) so the marker sits
                    on the colored bar at the score cell that matches
                    combinedScore. The DOCX generator burns an identical
                    diamond into the printed scale image at the same anchor, so
                    the on-screen and PDF indicators line up. */}
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    style={{
                      position: "absolute",
                      top: "33.65%",
                      left: `${combinedScore}%`,
                      transform: "translate(-50%, -50%)",
                      display: "block",
                      pointerEvents: "none",
                    }}
                    aria-hidden="true"
                  >
                    <polygon points="8,0 16,8 8,16 0,8" fill="#000" />
                  </svg>
                </div>

                <div className="text-center mt-2">
                  <span
                    className="badge badge-lg px-3 py-2"
                    style={{
                      backgroundColor: categoryInfo.color,
                      color: "#fff",
                      fontSize: "14px",
                    }}
                  >
                    Risk Tolerance: {riskCategory}
                  </span>
                </div>
              </div>
            </div>

            {/* Risk Tolerance - matches proposal step guide pattern */}
            <div className="form-group">
              <label>
                <strong>Risk Tolerance</strong>
              </label>

              {!this.props.strategySelected ? (
                <div className="alert alert-info py-2">
                  <small>
                    Please select a strategy in Section 2 to see available risk
                    tolerance options.
                  </small>
                </div>
              ) : (
                <select
                  name="riskTolerance"
                  id="riskTolerance"
                  className="form-control"
                  style={{
                    borderLeftColor: selectedPortfolioAvailable
                      ? "green"
                      : "#ced4da",
                    borderLeftWidth: "5px",
                  }}
                  value={selectedPortfolio}
                  onChange={(e) => onPortfolioChange(e.target.value)}
                >
                  <option value="">Select Risk Tolerance</option>
                  {availablePortfolios.map((p, index) => (
                    <option value={p} key={index}>
                      {p}
                    </option>
                  ))}
                </select>
              )}
              {scoredPortfolioUnavailable && (
                <div className="alert alert-warning py-2 mt-2 mb-0">
                  <small>
                    Currently, a {riskCategory} Portfolio is not available for
                    this strategy. Please select an available portfolio within
                    the override options below and provide a reason.
                  </small>
                </div>
              )}
            </div>

            {/* Portfolio Override — the label sits outside the completeness
                bar, which wraps the Yes/Not Applicable choice and, when "Yes",
                the reason it reveals. "Not Applicable" is the default valid
                state, so the bar is grey only while an override is in progress
                but undocumented. */}
            <div className="mt-3">
              <label className="mb-1">
                <strong>Portfolio Override</strong>
              </label>
              <div
                style={{
                  borderLeft: `5px solid ${
                    !portfolioOverride ||
                    (overrideReason &&
                      (overrideReason !== "Other" || overrideOtherText))
                      ? "green"
                      : "#ced4da"
                  }`,
                  paddingLeft: "12px",
                }}
              >
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="portfolioOverride"
                    id="portfolioOverrideYes"
                    checked={portfolioOverride === true}
                    onChange={() => onOverrideToggle(true)}
                    style={{ height: "15px", width: "15px" }}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="portfolioOverrideYes"
                  >
                    Yes
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="portfolioOverride"
                    id="portfolioOverrideNA"
                    checked={!portfolioOverride}
                    onChange={() => onOverrideToggle(false)}
                    style={{ height: "15px", width: "15px" }}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="portfolioOverrideNA"
                  >
                    Not Applicable
                  </label>
                </div>

                {portfolioOverride && (
                  <div className="mt-2">
                    <small className="form-text text-muted mb-2 d-block">
                      Given my unique circumstances, I wish to choose a
                      different risk tolerance and portfolio outside of the
                      options available within the risk tolerance category for
                      which I scored.
                    </small>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="overrideReason"
                        id="overridePortionReason"
                        value="The amount being invested is a portion of my overall portfolio"
                        checked={
                          overrideReason ===
                          "The amount being invested is a portion of my overall portfolio"
                        }
                        onChange={(e) => onOverrideReasonChange(e.target.value)}
                        style={{ height: "15px", width: "15px" }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="overridePortionReason"
                      >
                        The amount being invested is a portion of my overall
                        portfolio
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="overrideReason"
                        id="overrideOtherReason"
                        value="Other"
                        checked={overrideReason === "Other"}
                        onChange={(e) => onOverrideReasonChange(e.target.value)}
                        style={{ height: "15px", width: "15px" }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="overrideOtherReason"
                      >
                        Other (must document)
                      </label>
                    </div>
                    {overrideReason === "Other" && (
                      <>
                        <input
                          type="text"
                          className="form-control mt-2"
                          style={{
                            borderLeftColor: overrideOtherText
                              ? "green"
                              : "#ced4da",
                            borderLeftWidth: "5px",
                          }}
                          value={overrideOtherText}
                          onChange={(e) =>
                            onOverrideOtherTextChange(e.target.value)
                          }
                          placeholder="Please document your reason"
                          maxLength={200}
                        />
                        <small className="form-text text-muted text-right d-block">
                          {(overrideOtherText || "").length}/200
                        </small>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="alert alert-info py-2">
            <small>
              Please answer all questions above to calculate your Total Risk
              Tolerance Score.
            </small>
          </div>
        )}
      </div>
    );
  }
}

export default RiskQuestionnaire;
